import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { DeviceService } from '../../services/device.service';
import { SpaceContextService } from '../../services/space-context.service';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule],
  template: `
    <div class="flex h-screen bg-zaxis-dark text-white overflow-hidden">
      <!-- Sidebar -->
      <aside
        class="flex flex-col border-r border-zaxis-border bg-zaxis-dark transition-all duration-300 ease-in-out z-20"
        [class.w-64]="!collapsed"
        [class.w-[72px]]="collapsed">

        <!-- Logo -->
        <div class="flex items-center gap-3 px-5 py-6 border-b border-zaxis-border min-h-[76px]">
          <div class="w-9 h-9 border border-zaxis-accent bg-zaxis-accent/10 flex items-center justify-center flex-shrink-0 shadow-[0_0_10px_rgba(6,182,212,0.2)]">
            <span class="text-zaxis-accent font-mono font-black text-sm">Z</span>
          </div>
          <div *ngIf="!collapsed" class="overflow-hidden">
            <h1 class="text-sm font-bold tracking-tight text-white whitespace-nowrap uppercase">Kairos Zaxis</h1>
            <p class="text-[10px] text-zaxis-accent font-mono tracking-widest whitespace-nowrap">TELEMETRÍA XR</p>
          </div>
        </div>

        <!-- Context Selector -->
        <div class="border-b border-zaxis-border px-3 py-3" *ngIf="spaceContext.availableContexts$ | async as contexts">
          <div class="relative" *ngIf="contexts.length > 0">
            <!-- Custom Context Dropdown (Expanded View) -->
            <div *ngIf="!collapsed" class="relative">
              <!-- Trigger -->
              <button
                (click)="isContextDropdownOpen = !isContextDropdownOpen"
                class="w-full bg-black/40 border border-zaxis-border/50 text-white text-[10px] font-mono uppercase px-3 py-2 flex items-center justify-between hover:border-zaxis-accent transition-colors truncate">
                <span class="truncate pr-2">{{ (spaceContext.activeContext$ | async)?.name || 'Selecciona...' }}</span>
                <i class="ph ph-caret-down text-gray-500 text-xs"></i>
              </button>
              
              <!-- Dropdown Menu -->
              <div *ngIf="isContextDropdownOpen" class="absolute top-full left-0 mt-1 min-w-[320px] max-h-64 overflow-y-auto bg-black border border-zaxis-border z-50 shadow-lg shadow-black/50 p-1">
                <button
                  *ngFor="let ctx of contexts"
                  (click)="selectContext(ctx.id)"
                  class="w-full text-left px-3 py-2 text-[10px] font-mono uppercase text-gray-400 hover:bg-zaxis-dark transition-colors border-b border-zaxis-border/30 last:border-0 group flex flex-col gap-0.5">
                  <span *ngIf="formatContextName(ctx.name).path" class="text-[9px] text-gray-600 truncate w-full group-hover:text-gray-400 transition-colors">
                    {{ formatContextName(ctx.name).path }}
                  </span>
                  <span class="font-bold text-white group-hover:text-zaxis-accent transition-colors" [class.text-zaxis-accent]="(spaceContext.activeContext$ | async)?.id === ctx.id">
                    {{ formatContextName(ctx.name).leaf }}
                  </span>
                </button>
              </div>
              
              <!-- Invisible Backdrop for closing -->
              <div *ngIf="isContextDropdownOpen" class="fixed inset-0 z-40" (click)="isContextDropdownOpen = false"></div>
            </div>
            
            <!-- Collapsed view -->
            <div *ngIf="collapsed" class="w-9 h-9 mx-auto border border-zaxis-border/50 bg-black/40 flex items-center justify-center cursor-help group relative">
              <i class="ph ph-briefcase text-gray-400 group-hover:text-zaxis-accent transition-colors"></i>
              <span class="sidebar-tooltip flex flex-col gap-0.5 text-left">
                <strong class="font-bold">Contexto Activo</strong>
                <span class="text-[9px] text-gray-400 font-normal whitespace-normal w-44 leading-tight">{{ (spaceContext.activeContext$ | async)?.name || 'Cargando...' }}</span>
              </span>
            </div>
          </div>
        </div>

        <!-- Nav Items -->
        <nav class="flex-1 py-4 space-y-1">
          <a routerLink="/dashboard" routerLinkActive="border-zaxis-accent bg-white/5 text-white"
             class="flex items-center gap-3 px-5 py-3 border-l-2 border-transparent text-gray-500 hover:bg-white/5 hover:text-gray-300 transition-all group relative cursor-pointer">
            <i class="ph ph-chart-bar text-xl flex-shrink-0 w-6 text-center group-hover:text-zaxis-accent transition-colors"></i>
            <span *ngIf="!collapsed" class="text-[11px] font-mono uppercase tracking-widest whitespace-nowrap">Panel</span>
            <span *ngIf="collapsed" class="sidebar-tooltip flex flex-col gap-0.5 text-left">
              <strong class="font-bold">Panel</strong>
              <span class="text-[9px] text-gray-400 font-normal whitespace-normal w-44 leading-tight">Métricas globales y estado de la plataforma.</span>
            </span>
          </a>

          <a routerLink="/sessions" routerLinkActive="border-zaxis-accent bg-white/5 text-white"
             class="flex items-center gap-3 px-5 py-3 border-l-2 border-transparent text-gray-500 hover:bg-white/5 hover:text-gray-300 transition-all group relative cursor-pointer">
            <i class="ph ph-film-strip text-xl flex-shrink-0 w-6 text-center group-hover:text-zaxis-accent transition-colors"></i>
            <span *ngIf="!collapsed" class="text-[11px] font-mono uppercase tracking-widest whitespace-nowrap">Sesiones</span>
            <span *ngIf="collapsed" class="sidebar-tooltip flex flex-col gap-0.5 text-left">
              <strong class="font-bold">Sesiones</strong>
              <span class="text-[9px] text-gray-400 font-normal whitespace-normal w-44 leading-tight">Historial completo de telemetría XR.</span>
            </span>
          </a>

          <a routerLink="/logs" routerLinkActive="border-zaxis-accent bg-white/5 text-white"
             class="flex items-center gap-3 px-5 py-3 border-l-2 border-transparent text-gray-500 hover:bg-white/5 hover:text-gray-300 transition-all group relative cursor-pointer">
            <i class="ph ph-list-dashes text-xl flex-shrink-0 w-6 text-center group-hover:text-zaxis-accent transition-colors"></i>
            <span *ngIf="!collapsed" class="text-[11px] font-mono uppercase tracking-widest whitespace-nowrap">Logs</span>
            <span *ngIf="collapsed" class="sidebar-tooltip flex flex-col gap-0.5 text-left">
              <strong class="font-bold">Logs</strong>
              <span class="text-[9px] text-gray-400 font-normal whitespace-normal w-44 leading-tight">Eventos y excepciones en modo texto puro.</span>
            </span>
          </a>

          <a routerLink="/app-models" routerLinkActive="border-zaxis-accent bg-white/5 text-white"
             class="flex items-center gap-3 px-5 py-3 border-l-2 border-transparent text-gray-500 hover:bg-white/5 hover:text-gray-300 transition-all group relative cursor-pointer">
            <i class="ph ph-cube text-xl flex-shrink-0 w-6 text-center group-hover:text-zaxis-accent transition-colors"></i>
            <span *ngIf="!collapsed" class="text-[11px] font-mono uppercase tracking-widest whitespace-nowrap">Modelos 3D</span>
            <span *ngIf="collapsed" class="sidebar-tooltip flex flex-col gap-0.5 text-left">
              <strong class="font-bold">Modelos 3D</strong>
              <span class="text-[9px] text-gray-400 font-normal whitespace-normal w-44 leading-tight">Gestiona modelos vinculados por versión.</span>
            </span>
          </a>

          <a *ngIf="(auth.systemRole$ | async) === 'SUPER_ADMIN'" routerLink="/organizations" routerLinkActive="border-zaxis-accent bg-white/5 text-white"
             class="flex items-center gap-3 px-5 py-3 border-l-2 border-transparent text-gray-500 hover:bg-white/5 hover:text-gray-300 transition-all group relative cursor-pointer">
            <i class="ph ph-buildings text-xl flex-shrink-0 w-6 text-center group-hover:text-zaxis-accent transition-colors"></i>
            <span *ngIf="!collapsed" class="text-[11px] font-mono uppercase tracking-widest whitespace-nowrap">Instituciones</span>
            <span *ngIf="collapsed" class="sidebar-tooltip flex flex-col gap-0.5 text-left">
              <strong class="font-bold">Instituciones</strong>
              <span class="text-[9px] text-gray-400 font-normal whitespace-normal w-44 leading-tight">Gestión global de clientes y organizaciones.</span>
            </span>
          </a>

          <a routerLink="/devices" routerLinkActive="border-zaxis-accent bg-white/5 text-white"
             class="flex items-center gap-3 px-5 py-3 border-l-2 border-transparent text-gray-500 hover:bg-white/5 hover:text-gray-300 transition-all group relative cursor-pointer">
            <i class="ph ph-device-mobile text-xl flex-shrink-0 w-6 text-center group-hover:text-zaxis-accent transition-colors"></i>
            <span *ngIf="!collapsed" class="text-[11px] font-mono uppercase tracking-widest whitespace-nowrap">Dispositivos</span>
            <span *ngIf="collapsed" class="sidebar-tooltip flex flex-col gap-0.5 text-left">
              <strong class="font-bold">Dispositivos</strong>
              <span class="text-[9px] text-gray-400 font-normal whitespace-normal w-44 leading-tight">Gestión de cascos XR vinculados.</span>
            </span>
          </a>

          <button (click)="showClaimPanel = true"
             class="flex items-center gap-3 px-5 py-3 border-l-2 border-transparent text-gray-500 hover:bg-white/5 hover:text-gray-300 transition-all group relative cursor-pointer w-full text-left">
            <i class="ph ph-plus text-xl flex-shrink-0 w-6 text-center group-hover:text-zaxis-success transition-colors"></i>
            <span *ngIf="!collapsed" class="text-[11px] font-mono uppercase tracking-widest whitespace-nowrap">Vincular Disp.</span>
            <span *ngIf="collapsed" class="sidebar-tooltip flex flex-col gap-0.5 text-left">
              <strong class="font-bold text-zaxis-success">Vincular Dispositivo</strong>
              <span class="text-[9px] text-gray-400 font-normal whitespace-normal w-44 leading-tight">Registrar un nuevo casco en el sistema.</span>
            </span>
          </button>
        </nav>

        <!-- Bottom -->
        <div class="border-t border-zaxis-border py-4 space-y-1">
          <button (click)="collapsed = !collapsed"
                  class="flex items-center gap-3 px-5 py-3 border-l-2 border-transparent text-gray-500 hover:bg-white/5 hover:text-white transition-all group relative w-full text-left">
            <i class="ph ph-caret-double-left text-xl flex-shrink-0 w-6 text-center transition-transform" [class.rotate-180]="collapsed"></i>
            <span *ngIf="!collapsed" class="text-[11px] font-mono uppercase tracking-widest whitespace-nowrap">Colapsar</span>
            <span *ngIf="collapsed" class="sidebar-tooltip flex flex-col gap-0.5 text-left">
              <strong class="font-bold">Expandir Panel</strong>
              <span class="text-[9px] text-gray-400 font-normal whitespace-normal w-44 leading-tight">Mostrar etiquetas del menú lateral.</span>
            </span>
          </button>

          <button (click)="logout()"
                  class="flex items-center gap-3 px-5 py-3 border-l-2 border-transparent text-gray-500 hover:bg-zaxis-danger/10 hover:text-zaxis-danger hover:border-zaxis-danger transition-all group relative w-full text-left">
            <i class="ph ph-sign-out text-xl flex-shrink-0 w-6 text-center"></i>
            <span *ngIf="!collapsed" class="text-[11px] font-mono uppercase tracking-widest whitespace-nowrap">Cerrar Sesión</span>
            <span *ngIf="collapsed" class="sidebar-tooltip flex flex-col gap-0.5 text-left">
              <strong class="font-bold text-zaxis-danger">Cerrar Sesión</strong>
              <span class="text-[9px] text-gray-400 font-normal whitespace-normal w-44 leading-tight">Finalizar la sesión actual del usuario.</span>
            </span>
          </button>
        </div>
      </aside>

      <!-- Main Content -->
      <main class="flex-1 overflow-auto">
        <router-outlet></router-outlet>
      </main>

      <!-- Claim Device Slide-over Panel -->
      <div *ngIf="showClaimPanel" class="fixed inset-0 z-50 flex justify-end">
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" (click)="showClaimPanel = false"></div>

        <!-- Panel -->
        <div class="relative w-full max-w-md bg-zaxis-card border-l border-zaxis-border shadow-none flex flex-col animate-panel-in">
          <div class="flex items-center justify-between p-6 border-b border-zaxis-border">
            <h2 class="text-[11px] font-mono uppercase tracking-widest text-gray-400">Vincular Dispositivo</h2>
            <button (click)="showClaimPanel = false" class="text-gray-500 hover:text-white transition-colors text-xl font-mono">&times;</button>
          </div>

          <div class="flex-1 p-6 space-y-6">
            <div>
              <label class="block text-[11px] text-gray-500 font-mono uppercase tracking-widest mb-2">Código de Emparejamiento</label>
              <input
                #codeInput
                type="text"
                class="w-full bg-zaxis-dark border border-zaxis-border px-4 py-3 text-white focus:outline-none focus:border-zaxis-accent transition-all font-mono tracking-widest uppercase rounded-none"
                placeholder="X7K-9M2"
                maxlength="7">
            </div>

            <div>
              <label class="block text-[11px] text-gray-500 font-mono uppercase tracking-widest mb-2">Nombre del Dispositivo (Opcional)</label>
              <input
                #nameInput
                type="text"
                class="w-full bg-zaxis-dark border border-zaxis-border px-4 py-3 text-white focus:outline-none focus:border-zaxis-accent transition-all rounded-none"
                placeholder="HQ-QUEST-01">
            </div>

            <div *ngIf="claimError" class="text-zaxis-danger text-[11px] font-mono tracking-wide bg-zaxis-danger/10 p-3 border border-zaxis-danger/20">
              {{ claimError }}
            </div>
            <div *ngIf="claimSuccess" class="text-zaxis-success text-[11px] font-mono tracking-wide bg-zaxis-success/10 p-3 border border-zaxis-success/20">
              {{ claimSuccess }}
            </div>

            <button
              (click)="claimDevice(codeInput.value, nameInput.value)"
              [disabled]="isClaiming"
              class="px-6 py-3 bg-zaxis-accent hover:bg-cyan-400 text-black font-mono font-bold uppercase tracking-widest transition-all disabled:opacity-50 disabled:cursor-not-allowed w-full rounded-none">
              {{ isClaiming ? 'VINCULANDO...' : 'VINCULAR DISPOSITIVO' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    @keyframes slideIn {
      from { transform: translateX(100%); }
      to { transform: translateX(0); }
    }
    .animate-slide-in {
      animation: slideIn 0.3s ease-out;
    }
    .sidebar-tooltip {
      position: absolute;
      left: calc(100% + 10px);
      top: 50%;
      transform: translateY(-50%);
      padding: 6px 12px;
      background: rgba(0,0,0,0.85);
      backdrop-filter: blur(8px);
      border: 1px solid rgba(255,255,255,0.1);
      border-radius: 8px;
      font-size: 11px;
      color: #e2e8f0;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.15s ease;
      z-index: 50;
      white-space: nowrap;
    }
    .group:hover .sidebar-tooltip { opacity: 1; }
  `]
})
export class LayoutComponent implements OnInit {
  public auth = inject(AuthService);
  private deviceService = inject(DeviceService);
  public spaceContext = inject(SpaceContextService);

  collapsed = true;
  showClaimPanel = false;
  isClaiming = false;
  claimError = '';
  claimSuccess = '';
  isContextDropdownOpen = false;

  ngOnInit() {
    this.spaceContext.loadContexts();
  }

  selectContext(id: string) {
    this.spaceContext.setActiveContextById(id);
    this.isContextDropdownOpen = false;
  }

  formatContextName(name: string): { path: string, leaf: string } {
    if (!name) return { path: '', leaf: '' };
    const parts = name.split(' / ');
    if (parts.length <= 1) return { path: '', leaf: name };
    const leaf = parts.pop() || '';
    return { path: parts.join(' / '), leaf };
  }

  claimDevice(code: string, name: string) {
    if (!code.trim()) return;

    this.isClaiming = true;
    this.claimError = '';
    this.claimSuccess = '';

    this.deviceService.claimDevice(code, name).subscribe({
      next: () => {
        this.claimSuccess = `Dispositivo ${code} vinculado exitosamente.`;
        this.isClaiming = false;
      },
      error: () => {
        this.claimError = 'No se pudo vincular el dispositivo. Verifica que el código sea correcto.';
        this.isClaiming = false;
      }
    });
  }

  logout() {
    this.auth.logout();
  }
}
